/**
 * InteractionPrompt — HUD element (extracted from PixiHUDRenderer.js in Phase 3D).
 *
 * create(hud) builds the PIXI objects into hud.elements.interactionPrompt,
 * position(hud) lays them out for hud.screenWidth/Height, update(hud, …) redraws
 * from game state. `hud` is the PixiHUDRenderer coordinator: it owns
 * hudContainer, elements, screen size, _createText() and send().
 */
import * as PIXI from 'pixi.js';

export function create(hud) {
  const container = new PIXI.Container();
  container.visible = false;
  hud.hudContainer.addChild(container);

  const bg = new PIXI.Graphics();
  container.addChild(bg);

  const instructionText = hud._createText('Hold E / X to open', 16, 0xffffff, true);
  instructionText.anchor.set(0.5, 0.5);
  container.addChild(instructionText);

  const barBg = new PIXI.Graphics();
  container.addChild(barBg);

  const barFill = new PIXI.Graphics();
  container.addChild(barFill);

  // The hold ring drawn around the container itself (see containerStyles.js)
  // carries the same progress, so this panel shows the bar and nothing more.
  hud.elements.interactionPrompt = {
    container,
    bg,
    instructionText,
    barBg,
    barFill,
    width: 260,
    height: 60
  };
  hud._positionInteractionPrompt();
}

export function position(hud) {
  const prompt = hud.elements.interactionPrompt;
  if (!prompt) return;

  const width = prompt.width;
  const height = prompt.height;
  const x = (hud.screenWidth - width) / 2;
  const y = hud.screenHeight - 180;

  prompt.container.x = x;
  prompt.container.y = y;

  prompt.bg.clear();
  prompt.bg.roundRect(0, 0, width, height, 10);
  prompt.bg.fill({ color: 0x000000, alpha: 0.7 });
  prompt.bg.setStrokeStyle({ width: 2, color: 0xffffff, alpha: 0.2 });
  prompt.bg.stroke();

  prompt.instructionText.x = width / 2;
  prompt.instructionText.y = 22;

  prompt.barX = 20;
  prompt.barY = height - 24;
  prompt.barWidth = width - 40;
  prompt.barHeight = 10;

  prompt.barBg.clear();
  prompt.barBg.roundRect(prompt.barX, prompt.barY, prompt.barWidth, prompt.barHeight, 5);
  prompt.barBg.fill({ color: 0xffffff, alpha: 0.15 });
}

export function update(hud, gameState) {
  const prompt = hud.elements.interactionPrompt;
  if (!prompt) return;

  const interaction = gameState.lootContainerInteraction || {};
  const containerId = gameState.nearestLootContainerId || interaction.activeContainerId;
  if (!containerId) {
    prompt.container.visible = false;
    return;
  }

  const container = gameState.lootContainers?.get(containerId);
  if (!container) {
    prompt.container.visible = false;
    return;
  }
  if (container.interactionMode === 'damage' && interaction.activeContainerId !== containerId) {
    prompt.container.visible = false;
    return;
  }

  const isInstant = container.interactionMode === 'instant';
  prompt.instructionText.text = isInstant ? 'Press E / X to open' : 'Hold E / X to open';
  prompt.container.visible = true;

  const showProgress = !isInstant && interaction.activeContainerId === containerId;
  prompt.barBg.visible = showProgress;
  prompt.barFill.visible = showProgress;

  if (!showProgress) {
    prompt.barFill.clear();
    return;
  }

  const progress = Math.max(0, Math.min(1, interaction.progress || 0));
  prompt.barFill.clear();
  prompt.barFill.roundRect(prompt.barX, prompt.barY, prompt.barWidth * progress, prompt.barHeight, 5);
  prompt.barFill.fill({ color: 0x72ffb8 });
}
